/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { CircularProgress, Menu, MenuItem } from '@mui/material';
import React, { useId, useState } from 'react';
import { CustomToggleButton } from '@opengeoweb/shared';
import type { layerTypes } from '@opengeoweb/store';
import { ArrowDropDownDown } from '@opengeoweb/theme';
import { useDataExplorerTranslation } from '../../utils/i18n';

interface DatasetAddRemoveButtonProps {
  layerName: string;
  foundLayer: layerTypes.Layer;
  disabled: boolean;
  addLayer: () => void;
  deleteLayer: () => void;
  isLoading?: boolean;
  addOptions?: { label: string; onClick: () => void }[];
}

const DatasetAddRemoveButton: React.FC<DatasetAddRemoveButtonProps> = ({
  layerName,
  foundLayer,
  disabled,
  addLayer,
  deleteLayer,
  isLoading = false,
  addOptions,
}: DatasetAddRemoveButtonProps) => {
  const { t } = useDataExplorerTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const hasMenu = Boolean(!foundLayer && addOptions?.length);
  const menuOpen = hasMenu && !!anchorEl && !disabled;
  const label = !foundLayer
    ? t('dataexplorer-add-dataset')
    : t('dataexplorer-remove-dataset');

  return (
    <>
      <CustomToggleButton
        aria-busy={isLoading}
        aria-controls={menuOpen ? menuId : undefined}
        aria-expanded={hasMenu ? menuOpen : undefined}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-label={label}
        data-testid={`datasetAddRemoveButton-${layerName}`}
        disabled={disabled}
        onClick={(event) => {
          if (hasMenu) {
            setAnchorEl(event.currentTarget);
          } else {
            !foundLayer ? addLayer() : deleteLayer();
          }
        }}
        selected={!!foundLayer}
        sx={{
          fontSize: '12px',
          width: '64px',
          ...(hasMenu && {
            '&&': {
              paddingX: '4px',
              gap: '4px',
              '& .MuiSvgIcon-root': {
                marginRight: 0,
                flexShrink: 0,
              },
            },
          }),
          '@container (max-width:300px)': {
            height: '24px !important',
          },
        }}
        variant="tool"
      >
        {isLoading ? <CircularProgress size={14} /> : label}
        {hasMenu ? <ArrowDropDownDown /> : null}
      </CustomToggleButton>
      {hasMenu ? (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          id={menuId}
          onClose={() => setAnchorEl(null)}
          open={menuOpen}
          slotProps={{ backdrop: { sx: { backgroundColor: 'transparent' } } }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {addOptions!.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                setAnchorEl(null);
                option.onClick();
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </>
  );
};

export default DatasetAddRemoveButton;
